import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, Pagination } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { BaseBarChart } from "../../../components/graphs/Bar/BaseBarChart"
import { Legends } from "../../../components/graphs/Legends"
import { KonanAPI } from "../../../services/KonanAPI"
import type { FeaturesCountsInsight } from "../../../types/generated/api/FeaturesCountsInsight"
import { InsightList } from "../../../types/generated/api/InsightList"
import { FEATURE_NESTING_SEPARATOR } from "../../../utils/constants"
import { ReportContext } from "../Context"
import type { InsightProps } from "../interfaces"

/**
 * FeaturesBreakdownContainer Component
 *
 * Provides a detailed breakdown of categorical features across different labels,
 * enabling analysis of feature value distributions.
 *
 * Features:
 * - Single feature selection with multi-label comparison
 * - Dynamic data processing for visualization
 * - Responsive layout with legend integration
 * - Intelligent empty state handling
 *
 * Data processing:
 * - Aggregates feature values across selected labels
 * - Maintains data consistency during selection changes
 * - Handles missing or incomplete data gracefully
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing the features breakdown visualization
 */
export const FeaturesBreakdownContainer = (props: InsightProps): React.ReactElement => {
  const { insight } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  const [selectedLabels, setSelectedLabels] = useState<{
    labels: string[] | undefined
    areAllValuesSelected: boolean
  }>({
    labels: report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA") ?? undefined,
    areAllValuesSelected: true,
  })

  const [selectedFeature, setSelectedFeature] = useState<string | undefined>(
    report?.features?.categorical?.[0] ?? undefined,
  )

  const [page, setPage] = useState<number>(0) // State for current page number
  const [pageSize, setPageSize] = useState<number>(10) // State for number of items per page

  // Fetch features breakdown insight data from the API
  const { data: featuresBreakdownInsight, isLoading: isLoadingFeaturesBreakdownInsight } = useQuery<
    AxiosResponse<FeaturesCountsInsight>,
    AxiosError
  >(
    [
      "features-breakdown-curve",
      projectId,
      report?.uuid,
      insight.uuid,
      selectedLabels.areAllValuesSelected,
      selectedLabels.labels,
      selectedFeature,
    ],
    () =>
      KonanAPI.fetchFeaturesCountInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
        labels: selectedLabels.labels ?? undefined,
        features: selectedFeature,
        features_category: "categorical",
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = _isEmpty(featuresBreakdownInsight?.data.features_counts_per_label)

  // Process and combine data for visualization
  const adjustedData = useMemo(() => {
    // Initialize data structures
    const labels: Set<string> = new Set()
    const data: Record<string, string>[] = []

    // Skip processing if no feature is selected
    if (!selectedFeature) return { labels: Array.from(labels), data }

    // Determine which labels to process
    const labelsToProcess = selectedLabels.labels?.length
      ? selectedLabels.labels
      : Object.keys(featuresBreakdownInsight?.data.features_counts_per_label ?? {})

    // Process each label's data
    labelsToProcess.forEach((label) => {
      labelsToProcess.length > 1 && labels.add(label)

      // Get feature values for current label
      const featureData = featuresBreakdownInsight?.data.features_counts_per_label?.[label]?.[selectedFeature] ?? {}

      // Process each feature value
      Object.entries(featureData).forEach(([key, value]) => {
        const existingDataPoint = data.find((item) => item.label === key)

        labelsToProcess.length === 1 && labels.add(key)

        if (existingDataPoint) {
          // Update existing data point
          existingDataPoint[label] = value as string
        } else {
          // Create new data point
          data.push({ [labelsToProcess.length > 1 ? label : key]: value as string, label: key })
        }
      })
    })

    return { labels: Array.from(labels), data }
  }, [selectedLabels, featuresBreakdownInsight?.data.features_counts_per_label, selectedFeature])

  /**
   * Handles the selection and deselection of labels in the distribution curve filter.
   *
   * This function manages three main scenarios:
   * 1. When all options are deselected: Resets to all labels being selected
   * 2. When "All Decisions" was previously selected: Switches to only the specifically selected labels
   * 3. When "All Decisions" is newly selected: Switches back to all labels being selected
   *
   * @param {string[]} selectedOptions - Array of currently selected label options from the dropdown
   *
   * @example
   * // When deselecting all options
   * handleLabelSelect([]) // Resets to all labels
   *
   * // When selecting specific labels after "All Decisions"
   * handleLabelSelect(['Label1', 'Label2']) // Sets only these specific labels
   *
   * // When selecting "All Decisions"
   * handleLabelSelect(['All Decisions']) // Resets to all available labels
   */
  const handleLabelSelect = (selectedOptions: string[]): void => {
    // If no options are selected, reset to all labels
    if (selectedOptions.length === 0) {
      setSelectedLabels({
        labels: report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA"),
        areAllValuesSelected: true,
      })
      return
    }

    // If previously all values were selected, filter to only specific selections
    if (selectedLabels.areAllValuesSelected) {
      setSelectedLabels({
        labels: selectedOptions.filter((option) => option !== t("FeaturesBreakdown.filters.decisions.all")),
        areAllValuesSelected: false,
      })
      return
    }

    // Handle toggling between all labels and specific selections
    setSelectedLabels({
      labels: selectedOptions.includes(t("FeaturesBreakdown.filters.decisions.all"))
        ? report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA")
        : selectedOptions,
      areAllValuesSelected: selectedOptions.includes(t("FeaturesBreakdown.filters.decisions.all")),
    })
  }

  return (
    <Grid item xs={12}>
      <BaseBarChart
        title={t("FeaturesBreakdown.title")}
        tooltipText={<Trans>{t("FeaturesBreakdown.tooltip")}</Trans>}
        data={adjustedData?.data.slice(page * pageSize, (page + 1) * pageSize) ?? []}
        keys={adjustedData?.labels ?? []}
        indexBy="label"
        isLoading={isLoadingFeaturesBreakdownInsight}
        graphHeight={360}
        groupMode={selectedLabels.labels?.length === 1 ? "stacked" : "grouped"}
        customHeaderComponent={
          <InputText
            value={
              selectedLabels.areAllValuesSelected
                ? t("FeaturesBreakdown.filters.decisions.all")
                : selectedLabels.labels?.length && selectedLabels.labels?.length > 0
                  ? t("FeaturesBreakdown.filters.decisions.selected", { count: selectedLabels.labels?.length })
                  : t("FeaturesBreakdown.filters.decisions.select")
            }
            options={report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA") ?? []}
            multipleModeProps={{
              isActive: true,
              hideChips: true,
              selectedValues: selectedLabels.areAllValuesSelected
                ? [t("FeaturesBreakdown.filters.decisions.all")]
                : (selectedLabels.labels ?? [t("FeaturesBreakdown.filters.decisions.all")]),
              setSelectedValues: (selectedOptions) => handleLabelSelect(selectedOptions as string[]),
              allowDeselect: !selectedLabels.areAllValuesSelected,
            }}
            isSelect
            hideDescription
            fullWidth
            menuProps={{ menuMaxContent: true }}
          />
        }
        subHeader={
          <Grid container item xs justifyContent={"space-between"}>
            <Grid item xs>
              <InputText
                value={selectedFeature ?? t("FeaturesBreakdown.filters.features.select")}
                handleChange={(e: InputChangeEvent) => setSelectedFeature(e.target.value as string)}
                optionsWithValues={[
                  ...(report?.features?.categorical
                    ?.filter((feature) => feature !== "NA")
                    .map((feature) => {
                      return { label: feature.replaceAll(FEATURE_NESTING_SEPARATOR, "."), value: feature }
                    }) ?? []),
                ]}
                width={300}
                isSelect
                hideDescription
                menuProps={{ menuMaxContent: true }}
              />
            </Grid>

            {/* Custom labels */}
            <Grid item xs display={"flex"}>
              <Grid item xs display={"inline-flex"} gap={1} justifyContent={"end"} alignItems={"center"} mr={1}>
                <Legends legends={adjustedData?.labels?.slice(0, 4) ?? []} slice={4} />
              </Grid>

              {insight.status === InsightList.status.SUCCEEDED && (
                <Grid item>
                  <Pagination
                    variant={"complex"}
                    totalCount={adjustedData.data.length}
                    page={page + 1}
                    onPageChange={(page: number) => setPage(page - 1)}
                    pageSize={pageSize}
                    onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
                    hidePageSize
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        }
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("FeaturesBreakdown.unavailable") }
              : undefined
        }
      />
    </Grid>
  )
}
